/**
 * B76B lane B — THE SEAM between the boundary surfaces (lane B) and the
 * athlete membership sheet (screen 2b, lane C).
 *
 * ## Why a seam and not a direct import
 *
 * Every boundary CTA in the coach-athlete paywall opens the SAME sheet
 * (handoff §Interactions: "Boundary CTAs (1a footer row, 1b, 1c, 4b) all open
 * the same membership sheet (2b)"). Lane B owns the four call sites; lane C
 * owns the sheet. Wiring the call sites to a concrete component would make
 * lane B un-landable until lane C lands, and would give four surfaces four
 * chances to disagree about how the sheet is opened.
 *
 * So the call sites depend on this context, never on the sheet. Lane B ships
 * the affordance and the four callers; lane C ships the provider and the
 * surface. Neither lane blocks the other, and the sheet has exactly one
 * entry point.
 *
 * ## The contract lane C fills
 *
 * Lane C mounts `<AthleteMembershipSheetProvider value={{ open }}>` above the
 * routes (App.tsx, alongside the other app-level mounts) and renders the sheet
 * itself. `open(origin)` is called with the surface the reader tapped from, so
 * lane C can attribute the purchase without lane B knowing anything about
 * attribution.
 *
 * ## What happens before lane C lands
 *
 * `useOpenAthleteMembershipSheet()` returns a stable no-op when no provider is
 * mounted. The rows and CTAs RENDER (they are part of the ratified design and
 * their copy is pinned by tests); tapping one does nothing until the provider
 * exists. This is a deliberate seam, not an unfinished edge: the no-op is the
 * documented default, so there is no TODO to forget and no half-wired CTA that
 * throws in production if the lanes land out of order.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * Which boundary surface the reader opened the sheet from. Lane C uses this
 * for attribution / origin-return (handoff §Interactions: "Purchase
 * cancel/failure → return to origin surface, no state change").
 */
export type AthleteMembershipOrigin =
  /** 1a — the persistent "Train yourself" rail footer row. */
  | 'rail_footer'
  /** 1b — the /chat coach boundary card CTA. */
  | 'chat_boundary'
  /** 1c — the full-screen athlete-surface boundary CTA. */
  | 'athlete_surface_boundary'
  /** 4b — the Settings "This account" card link. */
  | 'settings_this_account';

export interface AthleteMembershipSheetApi {
  /** Open the athlete membership sheet (screen 2b). */
  open: (origin: AthleteMembershipOrigin) => void;
}

const AthleteMembershipSheetContext = createContext<AthleteMembershipSheetApi | null>(null);

export function AthleteMembershipSheetProvider({
  value,
  children,
}: {
  value: AthleteMembershipSheetApi;
  children: ReactNode;
}): JSX.Element {
  // Memo on the one field so a provider that rebuilds its object literal per
  // render doesn't re-render every boundary surface in the tree.
  const api = useMemo<AthleteMembershipSheetApi>(() => ({ open: value.open }), [value.open]);
  return (
    <AthleteMembershipSheetContext.Provider value={api}>
      {children}
    </AthleteMembershipSheetContext.Provider>
  );
}

/** Stable no-op so callers get referential identity when no provider exists. */
const NOOP = (): void => {};

/**
 * The affordance every boundary CTA calls. Returns a stable opener bound to
 * `origin`; a no-op until lane C mounts the provider.
 */
export function useOpenAthleteMembershipSheet(
  origin: AthleteMembershipOrigin,
): () => void {
  const api = useContext(AthleteMembershipSheetContext);
  const open = api?.open;
  return useMemo(() => (open ? () => open(origin) : NOOP), [open, origin]);
}

/**
 * Is a real sheet mounted? Lane B does NOT branch its rendering on this (the
 * design says the rows render either way) — it exists so lane C's own tests
 * can assert the provider is reaching the call sites.
 */
export function useAthleteMembershipSheetPresent(): boolean {
  return useContext(AthleteMembershipSheetContext) != null;
}
